import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";

const LANG_KEY = "weavevo_lang";
const LANG_FALLBACK_KEY = "weavevo_language";

/**
 * If your user profile route differs, change this once.
 * Examples: "/profile", "/user", "/users"
 */
const USER_PROFILE_BASE = "/profile";

function safeReadLang() {
  try {
    const raw =
      localStorage.getItem(LANG_KEY) ||
      localStorage.getItem(LANG_FALLBACK_KEY) ||
      "";
    const norm = String(raw || "").toLowerCase().slice(0, 2);
    return norm === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

const I18N = {
  en: {
    title: "Search Weavevo",
    sub: "Search across projects, media, people and conversations.",
    placeholder: "Type keywords… (e.g., Paris, food, skyline, interview, akrem)",
    all: "All",
    projects: "Projects",
    media: "Media",
    people: "People",
    content: "Content",
    loading: "Searching…",
    noQueryTitle: "Type something to start searching",
    noQuerySub:
      "Use keywords, a project title, a creator name, or a place. We’ll show matching weaves, media and people.",
    noResults: "No results found.",
    tryExplore: "Try Explore",
    tryMyMedia: "My Media",
    open: "Open",
    copyRaw: "Copy raw data",
    source: "Source",
    proxyHintTitle: "Search API is not reachable (HTML returned)",
    proxyHintBody:
      "Your browser received an HTML page instead of JSON. This usually means Nginx routed the request to the React app (index.html) instead of the backend.",
  },
  fr: {
    title: "Rechercher sur Weavevo",
    sub: "Recherchez dans les projets, médias, utilisateurs et conversations.",
    placeholder: "Tapez des mots-clés… (ex : Paris, food, skyline, interview, akrem)",
    all: "Tout",
    projects: "Projets",
    media: "Médias",
    people: "Utilisateurs",
    content: "Contenu",
    loading: "Recherche…",
    noQueryTitle: "Tapez quelque chose pour lancer la recherche",
    noQuerySub:
      "Utilisez des mots-clés, un titre de projet, un nom de créateur ou un lieu. Nous afficherons les weaves, médias et utilisateurs correspondants.",
    noResults: "Aucun résultat.",
    tryExplore: "Ouvrir Explorer",
    tryMyMedia: "Mes Médias",
    open: "Ouvrir",
    copyRaw: "Copier les données",
    source: "Source",
    proxyHintTitle: "API de recherche indisponible (HTML reçu)",
    proxyHintBody:
      "Le navigateur a reçu une page HTML au lieu d’un JSON. Cela signifie généralement que Nginx redirige l’URL vers l’app React (index.html) plutôt que vers le backend.",
  },
};

function getParam(search, key) {
  try {
    return new URLSearchParams(search || "").get(key) || "";
  } catch {
    return "";
  }
}

function isJsonResponse(res) {
  const ct = String(res?.headers?.get?.("content-type") || "").toLowerCase();
  return ct.includes("application/json") || ct.includes("application/problem+json");
}

function looksLikeHtml(text) {
  const s = String(text || "").trim().slice(0, 200).toLowerCase();
  return s.startsWith("<!doctype") || s.startsWith("<html") || s.includes("<head") || s.includes("<body");
}

function normalizeResults(raw) {
  // Output: { projects: [], media: [], users: [], content: [], other: [] }
  const out = { projects: [], media: [], users: [], content: [], other: [] };
  if (!raw) return out;

  if (Array.isArray(raw)) {
    out.other = raw;
    return out;
  }

  if (typeof raw === "object") {
    const projects =
      raw.projects ||
      raw.project ||
      raw.weaves ||
      raw.weave ||
      raw.journeys ||
      raw.items?.projects ||
      [];
    const medias =
      raw.medias ||
      raw.media ||
      raw.assets ||
      raw.items?.media ||
      raw.items?.medias ||
      [];
    const users =
      raw.users ||
      raw.user ||
      raw.people ||
      raw.items?.users ||
      raw.items?.people ||
      [];
    const content =
      raw.content ||
      raw.posts ||
      raw.messages ||
      raw.comments ||
      raw.items?.content ||
      raw.items?.posts ||
      raw.items?.messages ||
      [];

    if (Array.isArray(projects)) out.projects = projects;
    if (Array.isArray(medias)) out.media = medias;
    if (Array.isArray(users)) out.users = users;
    if (Array.isArray(content)) out.content = content;

    const results = raw.results || raw.items || raw.hits || raw.data;
    if (
      Array.isArray(results) &&
      !out.projects.length &&
      !out.media.length &&
      !out.users.length &&
      !out.content.length
    ) {
      out.other = results;
    }

    // If still empty, keep whole object in "other" to at least show something.
    if (
      !out.projects.length &&
      !out.media.length &&
      !out.users.length &&
      !out.content.length &&
      !out.other.length
    ) {
      out.other = [raw];
    }

    return out;
  }

  return out;
}

function guessKind(item) {
  const t = String(
    item?.type ||
      item?.kind ||
      item?.entity ||
      item?.media_type ||
      item?.asset_type ||
      item?._kind ||
      ""
  ).toLowerCase();

  if (t.includes("user") || t.includes("person") || t.includes("account") || t.includes("profile")) return "user";
  if (t.includes("project") || t.includes("weave") || t.includes("journey")) return "project";
  if (t.includes("video") || t.includes("image") || t.includes("media") || t.includes("asset")) return "media";
  if (t.includes("message") || t.includes("post") || t.includes("comment") || t.includes("content")) return "content";

  // heuristic by fields
  if (item?.username || item?.email || item?.avatar || item?.avatar_url || item?.first_name || item?.last_name)
    return "user";
  if (item?.scenario || item?.status || item?.visibility || item?.contributors) return "project";
  if (item?.url || item?.thumbnail || item?.cloudinary || item?.duration || item?.mime) return "media";
  if (item?.message || item?.text || item?.body || item?.comment) return "content";

  return "other";
}

function getTitle(item, kind) {
  if (kind === "user") {
    const full =
      [item?.first_name, item?.last_name].filter(Boolean).join(" ").trim() ||
      [item?.firstname, item?.lastname].filter(Boolean).join(" ").trim();
    return (
      item?.displayName ||
      item?.fullName ||
      full ||
      item?.username ||
      item?.name ||
      item?.email ||
      "User"
    );
  }
  if (kind === "content") {
    const msg = item?.message || item?.text || item?.body || item?.comment || "";
    const s = String(msg).trim();
    return s ? (s.length > 80 ? `${s.slice(0, 77)}…` : s) : (item?.title || item?.name || "Content");
  }

  return (
    item?.title ||
    item?.name ||
    item?.project_title ||
    item?.projectName ||
    item?.filename ||
    item?.file_name ||
    item?.original_name ||
    "Untitled"
  );
}

function getSubtitle(item, kind) {
  if (kind === "user") {
    const u = item?.username ? `@${item.username}` : "";
    const email = item?.email || "";
    return [u, email].filter(Boolean).join(" • ");
  }
  if (kind === "content") {
    const p = item?.project_title || item?.projectTitle || item?.project_name || "";
    const who = item?.author_name || item?.author || item?.username || "";
    return [p, who ? `by ${who}` : ""].filter(Boolean).join(" • ");
  }
  if (kind === "project") {
    return item?.visibility || item?.status ? [item.visibility, item.status].filter(Boolean).join(" • ") : "";
  }
  if (kind === "media") {
    const mt = item?.mime || item?.type || item?.media_type || item?.asset_type || "";
    return mt ? String(mt) : "";
  }
  return "";
}


function safeText(v) {
  return v === 0 ? "0" : v == null ? "" : String(v);
}

function formatDateTime(raw) {
  const s = safeText(raw).trim();
  if (!s) return "";
  // Accept both ISO strings and "YYYY-MM-DD HH:mm:ss"
  const iso = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(iso);
  if (!Number.isNaN(d.getTime())) {
    try {
      return d.toLocaleString();
    } catch {
      return d.toISOString();
    }
  }
  return s;
}

function getContentFields(item) {
  const body = safeText(
    item?.body ??
      item?.message ??
      item?.text ??
      item?.comment ??
      item?.content ??
      item?.value ??
      ""
  ).trim();

  const sender = safeText(
    item?.sender ??
      item?.author_name ??
      item?.author ??
      item?.username ??
      item?.from ??
      item?.created_by ??
      ""
  ).trim();

  const createdAt = safeText(item?.created_at ?? item?.createdAt ?? item?.date ?? item?.timestamp ?? "").trim();

  const threadId = safeText(item?.thread_id ?? item?.threadId ?? item?.thread ?? item?.conversation_id ?? "").trim();

  const projectId = safeText(
    item?.projectId ?? item?.project_id ?? item?.journeyId ?? item?.journey_id ?? item?.weave_id ?? ""
  ).trim();

  return { body, sender, createdAt, threadId, projectId };
}


function getId(item) {
  return item?.id || item?.projectId || item?.project_id || item?.mediaId || item?.media_id || item?.userId || item?.user_id || item?._id;
}

function buildLink(kind, item) {
  const id = getId(item);
  if (!id) return null;

  if (kind === "project") return `/project/${id}`;
  if (kind === "media") return `/mediaDetails/${id}`;

  if (kind === "user") return `${USER_PROFILE_BASE}/${id}`;

  if (kind === "content") {
    const pid = item?.projectId || item?.project_id || item?.journeyId || item?.journey_id;
    if (pid) return `/project/${pid}`;
    return null;
  }

  return null;
}

async function safeParseJson(res) {
  // Some proxies mis-set content-type. We'll attempt to parse anyway.
  const text = await res.text();
  if (looksLikeHtml(text)) {
    const err = new Error("HTML_FALLBACK");
    err._html = true;
    err._bodyPreview = text.slice(0, 200);
    throw err;
  }
  try {
    return JSON.parse(text);
  } catch {
    const err = new Error("NON_JSON");
    err._bodyPreview = text.slice(0, 200);
    throw err;
  }
}

async function fetchFirstWorkingEndpoint({ q, token, signal }) {
  const encoded = encodeURIComponent(q || "");
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  /**
   * IMPORTANT:
   * We do NOT try "/search" here because it's the React route and usually returns HTML.
   * Keep candidates strictly backend-only paths.
   */
  const candidates = [
    `/weavevoSearch?q=${encoded}`,
    `/api/weavevoSearch?q=${encoded}`,
    `/globalSearch?q=${encoded}`,
    `/api/globalSearch?q=${encoded}`,
    `/projects/search?q=${encoded}`,
    `/media/search?q=${encoded}`,
    `/users/search?q=${encoded}`,
  ];

  let lastErr = null;

  for (const url of candidates) {
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 6000);

    try {
      const res = await fetch(url, {
        method: "GET",
        headers,
        signal: signal || ctrl.signal,
      });

      if (!res.ok) {
        lastErr = new Error(`${url} -> HTTP ${res.status}`);
        continue;
      }

      if (isJsonResponse(res)) {
        const data = await res.json();
        return { url, data };
      }

      // content-type is not json (often index.html) – still try to parse, otherwise fail nicely.
      try {
        const data = await safeParseJson(res);
        return { url, data };
      } catch (e) {
        if (e?._html) {
          lastErr = new Error(`${url} -> HTML (SPA fallback)`);
          lastErr._html = true;
        } else {
          lastErr = new Error(`${url} -> non-JSON response`);
        }
        continue;
      }
    } catch (e) {
      lastErr = e;
    } finally {
      clearTimeout(timeout);
    }
  }

  throw lastErr || new Error("No working endpoint");
}

function kindLabel(kind) {
  if (kind === "project") return "Project";
  if (kind === "media") return "Media";
  if (kind === "user") return "User";
  if (kind === "content") return "Content";
  return "Result";
}

function kindIcon(kind) {
  if (kind === "project") return "bi-collection";
  if (kind === "media") return "bi-camera-video";
  if (kind === "user") return "bi-people";
  if (kind === "content") return "bi-chat-left-text";
  return "bi-braces";
}

/** ***********************************
 * Inline CSS (same as DiscoverWeavevo)
 ************************************ */
const WeavevoSearchStyles = React.memo(function WeavevoSearchStyles() {
  return (
    <style>{`
      .btn-weave{
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: .5rem;
        box-shadow: 0 6px 18px rgba(0,0,0,.06);
      }
      .btn-weave:disabled{ box-shadow: none; }

      /* ✅ Force your Bootstrap-like PRIMARY palette on ALL buttons in this page (except links) */
      .wizard-scope .btn:not(.btn-link){
        --bs-btn-color: #fff;
        --bs-btn-bg: #0d6efd;
        --bs-btn-border-color: #0d6efd;
        --bs-btn-hover-color: #fff;
        --bs-btn-hover-bg: #0b5ed7;
        --bs-btn-hover-border-color: #0a58ca;
        --bs-btn-focus-shadow-rgb: 49, 132, 253;
        --bs-btn-active-color: #fff;
        --bs-btn-active-bg: #0a58ca;
        --bs-btn-active-border-color: #0a53be;
        --bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, .125);
        --bs-btn-disabled-color: #fff;
        --bs-btn-disabled-bg: #0d6efd;
        --bs-btn-disabled-border-color: #0d6efd;

        --bs-btn-padding-y: 0.25rem;
        --bs-btn-padding-x: 0.5rem;
        --bs-btn-font-size: 0.875rem;
        --bs-btn-border-radius: var(--bs-border-radius-sm);

        color: var(--bs-btn-color) !important;
        background-color: var(--bs-btn-bg) !important;
        border-color: var(--bs-btn-border-color) !important;
      }
      .wizard-scope .btn:not(.btn-link):hover{
        color: var(--bs-btn-hover-color) !important;
        background-color: var(--bs-btn-hover-bg) !important;
        border-color: var(--bs-btn-hover-border-color) !important;
      }
      .wizard-scope .btn:not(.btn-link):focus,
      .wizard-scope .btn:not(.btn-link):focus-visible{
        box-shadow: 0 0 0 .25rem rgba(var(--bs-btn-focus-shadow-rgb), .5) !important;
      }
      .wizard-scope .btn:not(.btn-link):active,
      .wizard-scope .btn:not(.btn-link).active{
        color: var(--bs-btn-active-color) !important;
        background-color: var(--bs-btn-active-bg) !important;
        border-color: var(--bs-btn-active-border-color) !important;
        box-shadow: var(--bs-btn-active-shadow) !important;
      }
      .wizard-scope .btn:not(.btn-link):disabled,
      .wizard-scope .btn:not(.btn-link).disabled{
        color: var(--bs-btn-disabled-color) !important;
        background-color: var(--bs-btn-disabled-bg) !important;
        border-color: var(--bs-btn-disabled-border-color) !important;
        opacity: .65 !important;
      }

      /* ✅ HERO subtitle + actions (no horizontal scrollbar) */
      .dv-hero-inline{
        display:flex;
        align-items:center;
        gap: 14px;
        flex-wrap: wrap;
        overflow-x: hidden;
      }
      .dv-hero-sub{
        color: rgba(0,0,0,.55);
        min-width: 320px;
        flex: 1 1 520px;
      }
      .dv-hero-actions{
        display:flex;
        align-items:center;
        gap: 8px;
        flex-wrap: wrap;
        flex: 0 0 auto;
      }
      @media (min-width: 1200px){
        .dv-hero-inline{ flex-wrap: nowrap; }
        .dv-hero-sub{
          white-space: nowrap;
          min-width: 0;
          flex: 1 1 auto;
        }
        .dv-hero-actions{ flex-wrap: nowrap; }
      }

      .dv-soft{
        background: rgba(0,0,0,.015);
        border: 1px solid rgba(0,0,0,.06);
        border-radius: 18px;
      }

      /* Search input style (keeps Discover typography) */
      .wv-search-input{
        background: transparent !important;
        box-shadow: none !important;
      }
      .wv-search-input::placeholder{
        color: rgba(0,0,0,.45);
      }

/* Content cards: show formatted message instead of raw JSON */
.wv-content-box{
  margin-top: 12px;
  margin-bottom: 0;
  padding: 12px;
  font-size: 13px;
  overflow: auto;
  max-height: 180px;
}
.wv-content-text{
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.35;
}
.wv-content-meta{
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  color: #6b7280;
  font-size: 12px;
}
.wv-meta-chip{
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,.06);
  background: rgba(0,0,0,.02);
}
.wv-meta-chip i{
  color: #6b7280;
}
    `}</style>
  );
});

export default function WeavevoSearch() {
  const auth = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const token = auth?.token || localStorage.getItem("kc_token") || "";

  const lang = useMemo(() => {
    const l = safeReadLang();
    try {
      const current = String(i18n?.language || "").toLowerCase().slice(0, 2);
      if (i18n?.changeLanguage && current !== l) i18n.changeLanguage(l).catch(() => {});
    } catch {}
    return l;
  }, [i18n]);

  const T = I18N[lang] || I18N.en;

  const qFromUrl = useMemo(() => {
    // Accept both ?q= and legacy ?query=
    return getParam(location.search, "q") || getParam(location.search, "query") || "";
  }, [location.search]);
  const [query, setQuery] = useState(qFromUrl || "");
  const [tab, setTab] = useState("all"); // all | projects | media | people | content
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState("");
  const [error, setError] = useState("");
  const [errorIsHtml, setErrorIsHtml] = useState(false);
  const [results, setResults] = useState({ projects: [], media: [], users: [], content: [], other: [] });

  const lastFetchRef = useRef({ q: "" });

  
  // Normalize legacy URL param: if we have ?query= but no ?q=, rewrite to ?q=
  useEffect(() => {
    const legacy = getParam(location.search, "query");
    const current = getParam(location.search, "q");
    if (!current && legacy) {
      try {
        const params = new URLSearchParams(location.search || "");
        params.set("q", legacy);
        params.delete("query");
        navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
      } catch {}
    }
  }, [location.pathname, location.search, navigate]);

  // Optional bridge: allow the top navbar to drive results without touching this page
  // Navbar can dispatch: window.dispatchEvent(new CustomEvent("weavevo:search", { detail: { q: "paris" } }))
  useEffect(() => {
    const handler = (e) => {
      const next = String(e?.detail?.q || "").trim();
      if (!next) return;
      try {
        const params = new URLSearchParams(location.search || "");
        params.set("q", next);
        params.delete("query");
        navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
      } catch {}
    };
    window.addEventListener("weavevo:search", handler);
    return () => window.removeEventListener("weavevo:search", handler);
  }, [location.pathname, location.search, navigate]);

// Keep input in sync when query param changes (navigation, back button…)
  useEffect(() => {
    setQuery(qFromUrl || "");
  }, [qFromUrl]);

  useEffect(() => {
    const q = String(qFromUrl || "").trim();
    if (!q) {
      lastFetchRef.current.q = "";
      setResults({ projects: [], media: [], users: [], content: [], other: [] });
      setError("");
      setErrorIsHtml(false);
      setSource("");
      setLoading(false);
      return;
    }

    let mounted = true;
    const ctrl = new AbortController();

    // Debounce to support the top navbar search (typing shouldn't spam the backend)
    const timer = setTimeout(() => {
      // Avoid refetch loop
      if (lastFetchRef.current.q === q) return;
      lastFetchRef.current.q = q;

      setLoading(true);
      setError("");
      setErrorIsHtml(false);
      setSource("");

      fetchFirstWorkingEndpoint({ q, token, signal: ctrl.signal })
        .then(({ url, data }) => {
          if (!mounted) return;
          setSource(url || "");
          setResults({
            projects: Array.isArray(data?.projects) ? data.projects : [],
            media: Array.isArray(data?.media) ? data.media : [],
            users: Array.isArray(data?.users) ? data.users : [],
            content: Array.isArray(data?.content) ? data.content : [],
            other: Array.isArray(data?.other) ? data.other : [],
          });
        })
        .catch((err) => {
          if (!mounted) return;
          const msg = String(err?.message || err || "");
          setError(msg || "Search failed.");
          setErrorIsHtml(msg.toLowerCase().includes("html") || msg.toLowerCase().includes("non-json"));
          setResults({ projects: [], media: [], users: [], content: [], other: [] });
        })
        .finally(() => {
          if (!mounted) return;
          setLoading(false);
        });
    }, 250);

    return () => {
      mounted = false;
      clearTimeout(timer);
      try {
        ctrl.abort();
      } catch {}
    };
  }, [qFromUrl, token]);

  const onSubmit = (e) => {
    try {
      e?.preventDefault?.();
    } catch {}
    const q = String(query || "").trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const visibleItems = useMemo(() => {
    const merged = [];

    const projects = Array.isArray(results.projects) ? results.projects : [];
    const media = Array.isArray(results.media) ? results.media : [];
    const users = Array.isArray(results.users) ? results.users : [];
    const content = Array.isArray(results.content) ? results.content : [];

    if (tab === "projects") return projects.map((x) => ({ kind: "project", item: x }));
    if (tab === "media") return media.map((x) => ({ kind: "media", item: x }));
    if (tab === "people") return users.map((x) => ({ kind: "user", item: x }));
    if (tab === "content") return content.map((x) => ({ kind: "content", item: x }));

    // all
    if (projects.length) merged.push(...projects.map((x) => ({ kind: "project", item: x })));
    if (media.length) merged.push(...media.map((x) => ({ kind: "media", item: x })));
    if (users.length) merged.push(...users.map((x) => ({ kind: "user", item: x })));
    if (content.length) merged.push(...content.map((x) => ({ kind: "content", item: x })));

    // If only "other", try to guess kind per item
    const other = Array.isArray(results.other) ? results.other : [];
    if (!merged.length && other.length) {
      merged.push(
        ...other.map((x) => {
          const kind = guessKind(x);
          return { kind, item: x };
        })
      );
    }

    return merged;
  }, [results, tab]);

  const hasQuery = String(qFromUrl || "").trim().length > 0;
  const hasAny =
    (results.projects?.length || 0) +
      (results.media?.length || 0) +
      (results.users?.length || 0) +
      (results.content?.length || 0) +
      (results.other?.length || 0) >
    0;

  const tabs = [
    { key: "all", label: T.all, icon: "bi-ui-checks-grid" },
    { key: "projects", label: T.projects, icon: "bi-collection" },
    { key: "media", label: T.media, icon: "bi-camera-video" },
    { key: "people", label: T.people, icon: "bi-people" },
    { key: "content", label: T.content, icon: "bi-chat-left-text" },
  ];

  return (
    <div className="container-fluid py-3 wizard-scope" style={{ overflowX: "hidden" }}>
      <div className="container" style={{ maxWidth: 1440, paddingTop:50 }}>
        {/* HERO */}
        <div>
          <h1 className="mt-3 mb-2" style={{ fontWeight: 500, letterSpacing: "-0.02em" }}>
            {T.title}
          </h1>

          <div className="dv-hero-inline">
            <span className="dv-hero-sub">{T.sub}</span>

            <div className="dv-hero-actions">
              <Link to="/discover-weavevo" className="btn btn-outline-secondary btn-weave">
                <i className="bi bi-compass" />
                <span className="d-none d-md-inline">Discover</span>
              </Link>

              <Link to="/explore" className="btn btn-outline-secondary btn-weave">
                <i className="bi bi-grid" />
                <span>{T.tryExplore}</span>
              </Link>

              <Link to="/myMedias" className="btn btn-outline-secondary btn-weave">
                <i className="bi bi-collection-play" />
                <span>{T.tryMyMedia}</span>
              </Link>
            </div>
          </div>
        </div>

        <hr className="my-4" style={{ opacity: 0.12 }} />

       

        {/* FILTER TABS */}
        <div className="d-flex flex-wrap gap-2 mt-3">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`btn btn-outline-secondary btn-weave ${tab === t.key ? "active" : ""}`}
              style={{ opacity: tab === t.key ? 1 : 0.86 }}
            >
              <i className={`bi ${t.icon}`} />
              <span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* RESULTS */}
        <div className="mt-3">
          {loading ? (
            <div className="card border-0 rounded-4 shadow-sm">
              <div className="card-body" style={{ padding: 18, color: "#6b7280" }}>
                <div className="d-flex align-items-center gap-2">
                  <div className="spinner-border spinner-border-sm" role="status" />
                  <span>{T.loading}</span>
                </div>
              </div>
            </div>
          ) : !hasQuery ? (
            <div className="card border-0 rounded-4 shadow-sm">
              <div className="card-body" style={{ padding: 18 }}>
                <div style={{ fontWeight: 600 }}>{T.noQueryTitle}</div>
                <div style={{ color: "#6b7280", marginTop: 8 }}>{T.noQuerySub}</div>
              </div>
            </div>
          ) : !hasAny ? (
            <div className="card border-0 rounded-4 shadow-sm">
              <div className="card-body" style={{ padding: 18 }}>
                <div style={{ fontWeight: 600 }}>{T.noResults}</div>

                {error ? (
                  <div style={{ color: "#b91c1c", marginTop: 10 }}>
                    <small>{error}</small>
                  </div>
                ) : null}

                {errorIsHtml ? (
                  <div
                    className="dv-soft"
                    style={{
                      marginTop: 14,
                      padding: 12,
                      borderRadius: 14,
                      background: "#fff7ed",
                      color: "#9a3412",
                      borderColor: "rgba(234, 88, 12, .22)",
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{T.proxyHintTitle}</div>
                    <div style={{ marginTop: 6, fontSize: 13 }}>{T.proxyHintBody}</div>
                    <div style={{ marginTop: 10, fontSize: 12, color: "#7c2d12" }}>
                      <code>Fix: proxy /weavevoSearch (and /users/search etc.) to your Node backend.</code>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="text-muted" style={{ marginTop: 6 }}>
                <small>{source ? `${T.source}: ${source}` : null}</small>
              </div>

              <div className="row g-3 mt-1">
                {visibleItems.map(({ kind, item }, idx) => {
                  const title = getTitle(item, kind);
                  const subtitle = getSubtitle(item, kind);
                  const link = buildLink(kind, item);
                  const id = getId(item);

                  const contentFields = kind === "content" ? getContentFields(item) : null;
                  const contentWhen = contentFields ? formatDateTime(contentFields.createdAt) : "";
                  const contentMsg = contentFields ? (contentFields.body || "").trim() : "";

                  return (
                    <div className="col-12 col-md-6" key={`${kind}-${id || idx}`}>
                      <div className="card border-0 rounded-4 shadow-sm h-100" style={{ overflow: "hidden" }}>
                        <div className="card-body" style={{ padding: 18 }}>
                          <div className="d-flex align-items-start justify-content-between gap-3">
                            <div style={{ flex: 1 }}>
                              <div className="d-flex align-items-start gap-2">
                                <i className={`bi ${kindIcon(kind)}`} style={{ marginTop: 2, color: "#6b7280" }} />
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{title}</div>
                                  <div style={{ color: "#6b7280", marginTop: 6 }}>
                                    <small>
                                      {kindLabel(kind)}
                                      {id ? ` • ID: ${id}` : ""}
                                      {subtitle ? ` • ${subtitle}` : ""}
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div style={{ flex: "0 0 auto" }}>
                              {link ? (
                                <Link to={link} className="btn btn-primary btn-weave">
                                  <span>{T.open}</span>
                                  <i className="bi bi-arrow-right" />
                                </Link>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-weave"
                                  onClick={() => {
                                    try {
                                      navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                                    } catch {}
                                  }}
                                  title={T.copyRaw}
                                >
                                  <i className="bi bi-braces" />
                                </button>
                              )}
                            </div>
                          </div>

                          {!link ? (
                            kind === "content" ? (
                              <div className="dv-soft wv-content-box" style={{ borderRadius: 14 }}>
                                <div className="wv-content-text">{contentMsg || title}</div>

                                {(contentFields?.sender || contentWhen || contentFields?.threadId || contentFields?.projectId) ? (
                                  <div className="wv-content-meta">
                                    {contentFields?.sender ? (
                                      <span className="wv-meta-chip">
                                        <i className="bi bi-person" />
                                        <span>{contentFields.sender}</span>
                                      </span>
                                    ) : null}

                                    {contentWhen ? (
                                      <span className="wv-meta-chip">
                                        <i className="bi bi-clock" />
                                        <span>{contentWhen}</span>
                                      </span>
                                    ) : null}

                                    {contentFields?.threadId ? (
                                      <span className="wv-meta-chip">
                                        <i className="bi bi-hash" />
                                        <span>Thread {contentFields.threadId}</span>
                                      </span>
                                    ) : null}

                                    {contentFields?.projectId ? (
                                      <span className="wv-meta-chip">
                                        <i className="bi bi-diagram-3" />
                                        <span>Project {contentFields.projectId}</span>
                                      </span>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <pre
                                className="dv-soft"
                                style={{
                                  marginTop: 12,
                                  marginBottom: 0,
                                  padding: 12,
                                  fontSize: 12,
                                  overflow: "auto",
                                  maxHeight: 140,
                                }}
                              >
{JSON.stringify(item, null, 2)}
                              </pre>
                            )
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <WeavevoSearchStyles />
      </div>
    </div>
  );
}
